import {Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import * as PIXI from 'pixi.js';
import Application = PIXI.Application;
import { Viewport } from 'pixi-viewport';
import {drawColumns, drawRows} from '../../pages/setup/setup-ground/ground-groups-map/ground-cell';

export interface GroundPosition {
  rfId: string;
  x: number;
  y: number;
  line: string;
  column: string;
  type: string;
  groupName: string;
  groupId: string;
  hasPallet: boolean;
  state: string;
  id: string;
}

@Component({
  selector: 'app-map',
  template: ''
})
export class MapViewComponent implements OnInit, OnDestroy {

  public app: Application;
  public viewPort: Viewport;

  public options;

  @Input()
  public devicePixelRatio = window.devicePixelRatio || 1;

  @Input()
  public mapMode: 'stock' | 'pigeonhole' = 'stock';

  @Output() 
  triggerLoader = new EventEmitter<boolean>();

  constructor(
    protected elementRef: ElementRef,
    protected ngZone: NgZone
  ) {}

  initMap(maxWidth?, maxHeight?) {
    const border = 40;
    const canvasHeight = this.elementRef.nativeElement.offsetHeight;
    const canvasWidth =  this.elementRef.nativeElement.offsetWidth;
    this.ngZone.runOutsideAngular(() => {
      const options = {
        backgroundColor: 0xffffff,
        width: canvasWidth,
        height: canvasHeight,
        resizeTo:  this.elementRef.nativeElement,
      };
      this.app = new Application(options);

      // Se o mapa tiver uma largura muito grande, minScale vai ser hardcoded em 0.5 para que aumente a visibilidade das posições no mapa
      const minScale = maxWidth >= 35000 ? 0.5 : undefined;
      const worldWidth = maxWidth ? maxWidth + 2 * border : undefined;
      const worldHeight = maxHeight ? maxHeight + 2 * border : undefined;
      const isPigeonhole = this.mapMode === 'pigeonhole';

      let effectiveWorldWidth = worldWidth;
      if (isPigeonhole && worldHeight && worldWidth) {
        const fitScale = canvasHeight / worldHeight;
        effectiveWorldWidth = Math.max(worldWidth, canvasWidth / fitScale);
      }

      this.viewPort = new Viewport({
        screenWidth: canvasWidth,
        screenHeight: canvasHeight,
        worldWidth: effectiveWorldWidth,
        worldHeight: worldHeight,
        interaction: this.app.renderer.plugins.interaction
      });

      this.app.stage.addChild(this.viewPort as any);

      if (minScale !== undefined) {
        this.viewPort.clampZoom({ minScale, maxScale: 20 });
      } else if (isPigeonhole && worldHeight) {
        const calculatedMinScale = this.viewPort.findFitHeight(worldHeight);
        this.viewPort.clampZoom({ minScale: calculatedMinScale, maxScale: 20 });
      } else if (worldWidth) {
        const calculatedMinScale = this.viewPort.findFitWidth(worldWidth);
        this.viewPort.clampZoom({ minScale: calculatedMinScale, maxScale: 20 });
      } else {
        this.viewPort.clampZoom({ minScale: 0.1, maxScale: 20 });
      }

      this.viewPort
        .drag()
        .decelerate()
        .wheel()
        .clamp({ underflow: 'center', left: true, top: true, right: true, bottom: true });
      
      // Fit the entire map in the viewport on initial load
      if (isPigeonhole && worldHeight) {
        this.viewPort.fitHeight(worldHeight, false);
      } else if (worldWidth && worldHeight) {
        this.viewPort.fit(false, worldWidth, worldHeight);
      } else {
        this.viewPort.fitWorld();
      }
    });
    this.elementRef.nativeElement.appendChild(this.app.view);
    // this.resize();
  }

  public goTo(x,y){
    this.viewPort.moveCenter(x, y);
  }

  ngOnInit(): void {
    this.initMap();
  }

  @HostListener('window:resize')
  public resize() {
    this.triggerLoader.emit(true);
    const canvasHeight = this.elementRef.nativeElement.offsetHeight;
    const canvasWidth = this.elementRef.nativeElement.offsetWidth;

    this.app.renderer.resize(canvasWidth, canvasHeight);
    this.viewPort.resize(canvasWidth, canvasHeight);
    setTimeout(() => {
      this.triggerLoader.emit(false);
    }, 1000);
  }

  public clearContent(){
    if (this.viewPort) {
      // @ts-ignore - Viewport extends Container and has children methods
      const viewport: any = this.viewPort;
      while(viewport.children && viewport.children.length > 0){
        const child = viewport.getChildAt(0);
        if (child && child.destroy) {
          child.destroy();
        }
        viewport.removeChild(child);
      }
    }
  }

  destroy() {
    this.app?.destroy(); // without the null operator, it throws an undefined error if user token is expired and app destroy is not executed.
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  drawGrades(groundPositions: GroundPosition[]) {
    const gradePositions = this.getMaxCoordinates(groundPositions);
    const columns = drawColumns(gradePositions.positionsX);
    for (const c of columns) {
      // @ts-ignore
      this.viewPort.addChild(c);
    }
    const rows = drawRows(gradePositions.positionsY);
    for (const r of rows) {
      // @ts-ignore
      this.viewPort.addChild(r);
    }
  }
  getMaxCoordinates(groundPositions) {
    const positionsX = [];
    const positionsY = [];
    let x = 0;
    let y = 0;
    for (const gp of groundPositions) {
      if (gp.x > x) { x = gp.x; }
      if (gp.y > y) { y = gp.y; }
    }
    let countX = 0;
    while (countX <= x) {
      positionsX.push(countX);
      countX++;
    }
    let countY = 0;
    while (countY <= y) {
      positionsY.push(countY);
      countY++;
    }

    return {positionsX, positionsY};
  }


}
